<template>
    <Modal
        :title="$t('protocol.add-my-vet')"
        :is-open="isOpen"
        :back-text="$t('nav.close')"
        @update:is-open="setOpen($event)"
    >
        <div class="space-y-5">
            <div class="bg-dark-light flex items-center gap-3 rounded-lg p-3">
                <img class="h-10 w-10 shrink-0 rounded-full object-cover" :src="horse?.avatar" :alt="horse?.name" />
                <div class="min-w-0">
                    <div class="text-text-base truncate text-sm font-semibold">{{ horse?.name }}</div>
                    <div v-if="horse?.breed" class="text-text-gray truncate text-xs">{{ horse.breed }}</div>
                </div>
            </div>

            <p class="text-text-darker text-sm leading-relaxed">
                {{ $t('protocol.vet-description') }}
                <a href="https://alogo.io/products/health/" class="text-brand-base" target="_blank">
                    {{ $t('nav.more-info') }}.
                </a>
            </p>

            <div class="space-y-3">
                <label class="block">
                    <span class="text-text-gray mb-1 block text-[10px] font-semibold tracking-wider uppercase">
                        {{ $t('login.email') }}
                    </span>
                    <input
                        v-model="vetEmail"
                        type="email"
                        placeholder="john.doe@example.com"
                        class="bg-dark-lighter border-border text-text-base placeholder:text-text-gray focus:border-brand-base h-11 w-full rounded-lg border px-3 text-sm outline-hidden"
                        @keyup.enter="shareAsVet"
                    />
                </label>

                <Button fit small :enabled="canSubmit && !isSubmitting" @click="shareAsVet">
                    {{ $t('team.invite') }}
                </Button>
            </div>
        </div>
    </Modal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'primevue/usetoast'

import useHorse from '@/lib/hooks/use-horses'

import Modal from '@/components/modals/Modal.vue'
import Button from '@/components/buttons/Primary.vue'

const props = defineProps({
    isOpen: { type: Boolean, default: false },
    horse: { type: Object, required: true },
})

const emits = defineEmits(['update:isOpen'])

const { t } = useI18n()
const toast = useToast()
const { shareHorse } = useHorse()

const vetEmail = ref('')
const isSubmitting = ref(false)

const canSubmit = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vetEmail.value.trim()))

const setOpen = (state) => {
    emits('update:isOpen', state)
}

const shareAsVet = async () => {
    if (!canSubmit.value || isSubmitting.value) return
    isSubmitting.value = true

    try {
        await shareHorse(props.horse.id, vetEmail.value.trim(), 'veterinarian')
        toast.add({ severity: 'success', summary: t('horse.sharing.invited'), life: 3000 })
        vetEmail.value = ''
        setOpen(false)
    } catch (error) {
        toast.add({ severity: 'warn', summary: error?.message, life: 3000 })
    } finally {
        isSubmitting.value = false
    }
}
</script>
